/**
 * @fileoverview ActiveOrgProvider — provides active organisation context to
 * the component tree.
 *
 * Split out of `org-context.ts` (react-refresh/only-export-components).
 * Mounted once in the dashboard layout.
 */

import { useState, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ActiveOrgContext,
  LOCAL_STORAGE_KEY,
  type OrgWithRole,
} from "./org-context";

/**
 * Provides active organisation context to the component tree.
 *
 * @example
 * <ActiveOrgProvider>
 *   <AppSidebar />
 *   <Outlet />
 * </ActiveOrgProvider>
 */
export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { data: userOrgs, isLoading } = useQuery(
    convexQuery(api.organizations.queries.getUserOrganizations, {}),
  );

  const [orgId, setOrgIdState] = useState<Id<"organizations"> | null>(() => {
    // Try to restore from localStorage
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? (stored as Id<"organizations">) : null;
  });

  // Auto-select org when data loads
  useEffect(() => {
    if (!userOrgs || userOrgs.length === 0) return;

    // If stored org is still valid, keep it
    if (orgId && userOrgs.some((o: OrgWithRole) => o._id === orgId)) return;

    // Otherwise, select the first org
    const firstOrg = userOrgs[0];
    setOrgIdState(firstOrg._id);
    localStorage.setItem(LOCAL_STORAGE_KEY, firstOrg._id);
  }, [userOrgs, orgId]);

  const setOrgId = (newOrgId: Id<"organizations">) => {
    setOrgIdState(newOrgId);
    localStorage.setItem(LOCAL_STORAGE_KEY, newOrgId);
  };

  const activeOrg = userOrgs?.find((o: OrgWithRole) => o._id === orgId) ?? null;

  return (
    <ActiveOrgContext.Provider
      value={{
        orgId,
        activeOrg,
        userOrgs: userOrgs ?? [],
        isLoading,
        setOrgId,
      }}
    >
      {children}
    </ActiveOrgContext.Provider>
  );
}
